import Route from '@ember/routing/route'
import { service } from '@ember/service'
import type { IntlService } from 'ember-intl'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { restartableTask, timeout } from 'ember-concurrency'
import { Product } from 'teacher-ui-client/utils/products'
import { toRequestScopeStudent } from 'teacher-ui-client/services/reporting'
import type { RequestScope, ReportingQueryPage, ReportingQuerySort } from 'teacher-ui-client/services/reporting'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import type ReadingEggspressDashboardStudentRoute from 'teacher-ui-client/reporting/ui-scope/rex/dashboard/student/route'
import type {
  AssessmentsOverviewReportData,
  AssessmentsEventsReportData,
} from 'teacher-ui-client/reporting/ui-scope/rex/dashboard/student/index/route'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import type FileDownload from 'teacher-ui-client/services/file-download'
import { formatExcel } from 'teacher-ui-client/utils/dates'
import { reportingFilename } from 'teacher-ui-client/utils/filename'
import type { Params as AssessmentsIndexParams } from 'teacher-ui-client/reporting/ui-scope/rex/dashboard/student/assessments/index/route'
import type { NormalizedInterval } from 'date-fns'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'

export default class extends Route {
  @service reporting!: ReportingService

  @service intl!: IntlService

  @service fileDownload!: FileDownload

  @service flashQueue!: FlashQueueService

  buildRouteInfoMetadata() {
    return {
      pageTitle: this.intl.t('pages.reporting.readingEggspress.dashboard.student.assessments.title'),
      exportOptions: [
        {
          exportType: ExportType.CSV,
          exportAction: () => this.exportCsv(),
        },
        { exportType: ExportType.print },
      ],
    }
  }

  model() {
    const { period } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>

    const { student } = this.modelFor(
      'reporting.ui-scope.rex.dashboard.student',
    ) as ModelFor<ReadingEggspressDashboardStudentRoute>
    const requestScope = toRequestScopeStudent(student.id)

    void this.fetchAssessmentsOverviewReportTask.perform(Product.REX, period, requestScope).catch(() => {})

    // performing the events task is left up to the sub-route, where it has page/sort query params.
    // this is done in a sub-route to ensure the tasks in this model are not recalled when those QPs change.

    return {
      period,
      requestScope,
      fetchAssessmentsOverviewReportTask: this.fetchAssessmentsOverviewReportTask,
      fetchAssessmentsEventsReportTask: this.fetchAssessmentsEventsReportTask,
    }
  }

  fetchAssessmentsOverviewReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchAssessmentsOverviewReport>) => {
      await timeout(200)
      return this.fetchAssessmentsOverviewReport(...args)
    },
  )

  fetchAssessmentsEventsReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchAssessmentsEventsReport>) => {
      await timeout(200)
      return this.fetchAssessmentsEventsReport(...args)
    },
  )

  fetchAssessmentsOverviewReport(product: Product, period: NormalizedInterval, requestScope: RequestScope) {
    const reportName = 'student-dashboard/assessments/overview'
    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })
    const query = this.reporting.buildQuery({ period })
    return this.reporting.fetchReport<AssessmentsOverviewReportData>(url, query)
  }

  fetchAssessmentsEventsReport(
    product: Product,
    sort: ReportingQuerySort,
    page: ReportingQueryPage,
    period: NormalizedInterval,
    requestScope: RequestScope,
  ) {
    const reportName = 'student-dashboard/assessments/events'
    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })
    const query = this.reporting.buildQuery({ sort, page, period })
    return this.reporting.fetchReport<AssessmentsEventsReportData>(url, query)
  }

  async exportCsv() {
    const { period } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>
    const { student } = this.modelFor(
      'reporting.ui-scope.rex.dashboard.student',
    ) as ModelFor<ReadingEggspressDashboardStudentRoute>
    const requestScope = toRequestScopeStudent(student.id)
    const { sort } = this.paramsFor(
      'reporting.ui-scope.rex.dashboard.student.assessments.index',
    ) as AssessmentsIndexParams
    const page = 'all'

    let overviewReport: Awaited<ReturnType<typeof this.fetchAssessmentsOverviewReport>>
    let eventsReport: Awaited<ReturnType<typeof this.fetchAssessmentsEventsReport>>
    try {
      overviewReport = await this.fetchAssessmentsOverviewReport(Product.REX, period, requestScope)
      eventsReport = await this.fetchAssessmentsEventsReport(Product.REX, sort, page, period, requestScope)
    } catch {
      this.flashQueue.addFail({ subtitle: this.intl.t('failedToExportReport') })
      return
    }

    const overviewHeader = [
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.totalAssessmentsCompleted'),
      `${this.intl.t(
        'components.reporting.tables.readingEggspress.dashboardStudent.headers.averageAssessmentScore',
      )} %`,
    ]
    const overviewRow = [
      overviewReport.overview.total_assessments_completed,
      overviewReport.overview.average_assessment_score,
    ]

    const eventHeader = [
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.date'),
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.classification'),
      `${this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.lexile')} L`,
      `${this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.score')} %`,
    ]
    const eventRows = eventsReport.events.map((ev) => [
      formatExcel(new Date(ev.created_at)),
      ev.classification,
      ev.lexile,
      ev.score,
    ])

    const overviewFilename = `${reportingFilename('assessments-overview', student.fullName, period)}.csv`
    const eventsFilename = `${reportingFilename('assessments-recent-activity', student.fullName, period)}.csv`

    this.fileDownload.downloadAsCsv([overviewHeader, overviewRow], { filename: overviewFilename, header: true })
    this.fileDownload.downloadAsCsv([eventHeader, ...eventRows], { filename: eventsFilename, header: true })
  }
}
